import { inject, Injectable } from "@angular/core";
import { MonitoringProductImageService } from "@domain/product/monitoring/monitoring-product-image.service";
import { MonitoringProductService } from "@domain/product/monitoring/monitoring-product.service";
import { UtilityProductService } from "@domain/product/monitoring/utility-product.service";
import { ProductService } from "@domain/product/product.service";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import { ProjectImageService } from "@domain/project/project-image.service";
import { FloorplanWorkspaceItemService } from "@project/floorplanner/floorplan-workspace-item.service";
import { FloorplanWorkspaceWarningSignService } from "@project/floorplanner/floorplan-workspace-warning-sign.service";
import {
  FloorplanExportLayers,
  FloorplanWorkspaceKonvaAdapter,
} from "@project/floorplanner/konva/floorplan-workspace-konva.adapter";
import { FloorplanDragDropService } from "@utils/floorplan-drag-drop.service";
import { map, Observable, of, switchMap } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class FloorplanToPngService {
  private floorplanWorkspaceItemService = inject(FloorplanWorkspaceItemService);
  private floorplanWorkspaceWarningSignService = inject(FloorplanWorkspaceWarningSignService);
  private dragDropService: FloorplanDragDropService = inject(FloorplanDragDropService);
  private productService: ProductService = inject(ProductService);
  private monitoringProductService = inject(MonitoringProductService);
  private monitoringProductImageService = inject(MonitoringProductImageService);
  private projectImageService = inject(ProjectImageService);
  private utilityProductService = inject(UtilityProductService);

  getPng(floorplan: Floorplan): Observable<string> {
    return this.prepareFloorplan(floorplan).pipe(
      map((floorplanWorkspace) => {
        const pngUrl = floorplanWorkspace.exportFloorplanToPngUrl();
        floorplanWorkspace.destroy();
        return pngUrl;
      }),
    );
  }

  getLayersForPdf(floorplan: Floorplan): Observable<FloorplanExportLayers> {
    return this.prepareFloorplan(floorplan).pipe(
      map((floorplanWorkspace) => {
        const layers = floorplanWorkspace.exportFloorplanLayersForPdf();
        floorplanWorkspace.destroy();
        return layers;
      }),
    );
  }

  private prepareFloorplan(floorplan: Floorplan): Observable<FloorplanWorkspaceKonvaAdapter> {
    const floorplanWorkspace = new FloorplanWorkspaceKonvaAdapter(
      this.dragDropService,
      this.productService,
      this.monitoringProductService,
      this.monitoringProductImageService,
      this.projectImageService,
      this.utilityProductService,
    );

    const container = document.createElement("div");
    return floorplanWorkspace.init(floorplan, container).pipe(
      switchMap(() => this.floorplanWorkspaceItemService.addItems(floorplan, floorplanWorkspace)),
      switchMap(() => floorplanWorkspace.waitForProductImages$?.() ?? of(undefined)),
      switchMap(() => floorplanWorkspace.waitForFloorplanImages$?.() ?? of(undefined)),
      switchMap(() => this.floorplanWorkspaceWarningSignService.updateWarningSigns(floorplan, floorplanWorkspace)),
      map(() => floorplanWorkspace),
    );
  }
}
